// Reporter Agent API — drops a job message into the reporter pipeline
// via the same AppSync endpoint as the Ad Agency, using jobType "TRIGGER_REPORTER_JOB".
// The AppSync resolver routes this to reporter-agent-queue → TriggerStepFunction → reporter-agent-sfn.
import { ApolloClient, InMemoryCache, createHttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import {
  TRIGGER_AD_AGENCY_JOB_MUTATION,
  GET_LIBRARY_CARD_QUERY,
} from "../queries/libraryCardQueries";
import { getAuthToken } from "../utils/authUtils";

const GRAPHQL_ENDPOINT = "https://m2jxbyhagffjthxtsiuwb2iiwe.appsync-api.us-east-1.amazonaws.com/graphql";

const httpLink = createHttpLink({ uri: GRAPHQL_ENDPOINT });

const authLink = setContext((_, { headers }) => {
  const token = getAuthToken();
  return {
    headers: {
      ...headers,
      ...(token ? { Authorization: token } : {}),
    },
  };
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
});

// Sends the full SFN input as the payload.
// Expected payload shape: { ticketId, tenantId, topic, tone, publicationTypes,
//   includeDocuments?, includeVideos?,
//   knowledgeSources: ["library" | "x20_publications" | "perplexity", ...],
//   includeCitations?: boolean,
//   previousBacklogId?, tweakInstructions?,
//   attachments?: [{ s3Key, s3Bucket, fileName, mimeType, size }] }
// `includeCitations`: when true the BE keeps inline numbered refs in the
// article body and a Sources block at the end (current default). When false
// the BE strips refs for a cleaner read but still records sources internally.
// `previousBacklogId` + `tweakInstructions`: do-over re-queue triggered from
// the queue UI on a READY_TO_POST item. The BE is expected to load the prior
// item's plan/article and apply the tweak as a layered instruction.
//
// ──────────────────────────────────────────────────────────────────────────
// BE handoff — formConfig round-trip:
// The FE's TicketHistoryPicker hydrates the brief form by reading prior
// reporter runs out of PROCESSEDVIDEOS (id prefix "reporter_"). For
// hydration to work, the BE pipeline should persist the inbound payload
// onto the PROCESSEDVIDEOS row under either a top-level `formConfig` field
// or inside a `REPORTERTICKET.formConfig` section. The FE checks both.
// Persist the full payload (topic, tone, publicationTypes, includeDocuments,
// includeVideos, knowledgeSources, includeCitations, attachments) verbatim
// — the FE setters expect the same shape it sent.
// ──────────────────────────────────────────────────────────────────────────
// `knowledgeSources` is a non-empty subset — the BE SFN/Lambda fans retrieval
// across each listed source. "library" is the existing tenant DynamoDB library;
// "x20_publications" is the shared X20 docs suite; "perplexity" is live web
// research via the Perplexity API.
// `attachments` are creator-uploaded images (snapshots of notes, screenshots)
// that the SFN/Lambda pipeline is expected to fetch from S3 and feed to a
// vision model so the topic text can reference them ("see snap").
export const triggerReporterRunApi = async (payload) => {
  const { data } = await client.mutate({
    mutation: TRIGGER_AD_AGENCY_JOB_MUTATION,
    variables: {
      input: {
        jobType: "TRIGGER_REPORTER_JOB",
        tenantId: payload.tenantId,
        payload: JSON.stringify(payload),
      },
    },
  });
  return data.triggerAdAgencyJob;
};

// Fetch a prior reporter run ticket (lives in PROCESSEDVIDEOS, id prefix
// "reporter_"). Returns the parsed `data` blob — the formConfig hydrates
// the brief form on TicketHistoryPicker selection.
export const getReporterTicketApi = async ({ tenantId, runId }) => {
  try {
    const { data } = await client.query({
      query: GET_LIBRARY_CARD_QUERY,
      variables: {
        input: {
          operationType: "GET_LIBRARY_CARD",
          tenantId,
          entityType: "PROCESSEDVIDEOS",
          entityId: runId,
        },
      },
      fetchPolicy: "no-cache",
    });
    const card = data?.getLibraryCard;
    if (!card || !card.data) return null;
    return typeof card.data === "string" ? JSON.parse(card.data) : card.data;
  } catch (err) {
    const msg = String(err?.message || "");
    if (msg.includes("not found")) return null;
    throw err;
  }
};
